// The first-run screen's background: children's faces distributed over a SPHERE
// that turns forever, plus the neon call-to-action that sits inside it.
//
// WHY CSS 3D AND NOT three.js/@react-three/fiber: the reference for this effect is
// a WebGL canvas (a Fibonacci sphere of billboarded cards under OrbitControls). That
// stack costs ~600-900 KB gzipped and drei's <Environment> pulls an HDR map off a
// CDN that is slow-or-blocked in Iran. This Mini App runs in the Bale/Eitaa Android
// WebView over Iranian mobile data, where first paint is the whole battle (it's why
// /auth/login returns children inline instead of costing a second round-trip). The
// same read is achievable with perspective + nested rotate3d on ~14 nodes for ~4 KB,
// no dependency, and it animates on the compositor instead of a rAF loop.
//
// HOW THE SPHERE WORKS (the part that is easy to break):
// Faces sit on a shell of radius --R via  rotateY(theta) rotateX(phi) translateZ(R),
// with theta/phi derived from a golden-ratio (Fibonacci) point set so they spread
// evenly instead of clumping at the poles. One shared wrapper spins about Y forever.
//
// Each face must keep FACING THE VIEWER while the shell turns, which CSS has no
// lookAt() for. It falls out of the transform algebra instead — every rotation is
// undone in reverse order by nested children, so the net rotation is the identity
// and only the translation survives:
//
//   .ff-spin   rotateY(S)                      (animated, shared)
//   .ff-node     rotateY(t) rotateX(p) translateZ(R)
//   .ff-un         rotateX(-p) rotateY(-t)
//   .ff-bb           rotateY(-S) translate(-50%,-50%)   (animated, mirrors .ff-spin)
//
//   rotateY(S)·rotateY(t)·rotateX(p)·rotateX(-p)·rotateY(-t)·rotateY(-S) = identity
//
// Consequences to respect:
//   • .ff-spin and .ff-bb MUST share one duration (--spin) and both be `linear`, or
//     the counter-rotation drifts out of phase and the faces slowly turn edge-on.
//   • The -50% centring goes AFTER rotateY(-S) in .ff-bb's keyframes. Put it first
//     and the face orbits around its own corner instead of staying centred.
//   • .ff-field carries `perspective` and `overflow:hidden` but NOT preserve-3d:
//     per spec, overflow on a preserve-3d element forces the scene to flatten. Every
//     element BETWEEN it and the image needs preserve-3d and must not clip.
//
// ASSETS: /public/faces/*.webp — transparent illustrated busts, 170px wide, ~20 KB
// each (16 faces ~ 316 KB). Illustrations rather than photos on purpose: no real
// child's likeness. Encode with:
//   ffmpeg -i in.png -vf "scale=170:-2,split[a][b];[a]palettegen=max_colors=160:
//     reserve_transparent=1[p];[b][p]paletteuse=dither=bayer:bayer_scale=3"
//     -c:v libwebp -lossless 1 -compression_level 6 out.webp
// ⚠️ LOSSLESS IS NOT OPTIONAL. Lossy WebP writes alpha=1 instead of 0 across the
// transparent area, which composites as a visible dark RECTANGLE around every face
// on the dark background — verified side by side, present with AND without the
// drop-shadow, at both yuva420p and yuva444p. The 160-colour palette is what buys
// the size back (44 KB -> 20 KB, visually identical at this scale).
// ⚠️ And CHECK THE ALPHA after encoding — too few colours makes the dither leak
// opaque pixels into the transparent corner (it does at 140px/192 colours):
//   ffmpeg -i out.webp -vf "alphaextract,crop=16:16:0:0" -f rawvideo -pix_fmt gray -
// must be all zeros.
import { useMemo } from "react";

import { C, TALE } from "../design-system.js";

// Every face in /public/faces. Add a file → add it here; the sphere cycles through
// whatever is in this list, so it works with 5 images or 20.
export const FACES = [
  "/faces/face1.webp",
  "/faces/face2.webp",
  "/faces/face3.webp",
  "/faces/face4.webp",
  "/faces/face5.webp",
  "/faces/face6.webp",
  "/faces/face7.webp",
  "/faces/face8.webp",
  "/faces/face9.webp",
  "/faces/face10.webp",
  "/faces/face11.webp",
  "/faces/face12.webp",
  "/faces/face13.webp",
  "/faces/face14.webp",
  "/faces/face15.webp",
  "/faces/face16.webp",
];

// Deterministic pseudo-random. Home re-renders whenever its data settles, so a real
// Math.random() would reshuffle the whole sphere mid-turn; this keeps every face on
// the same point for the life of the screen.
function rnd(i, salt) {
  const x = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

const GOLDEN = (1 + Math.sqrt(5)) / 2;
const DEG = 180 / Math.PI;

/**
 * A sphere of children's faces, turning forever.
 *
 * @param images  face URLs to distribute — ONE face per image, never repeated
 * @param count   faces on the shell. Defaults to every image, and is CLAMPED to
 *                images.length: two copies of the same child on one sphere is
 *                immediately obvious and cheap-looking. Add an image to FACES and
 *                it becomes a face on its own; there is no count to bump.
 * @param spin    seconds for one full revolution
 * @param opacity ceiling opacity, so the sphere never fights the foreground copy
 */
export function FlyingFaces({ images = FACES, count = images.length, spin = 64, opacity = 0.95 }) {
  const total = Math.min(count, images.length);
  const faces = useMemo(() => Array.from({ length: total }, (_, i) => {
    const r = (salt) => rnd(i + 1, salt);

    // Golden-ratio point set on the unit sphere — even coverage, no clumping.
    const y    = total === 1 ? 0 : 1 - (i / (total - 1)) * 2;   // +1 (top) → -1
    const ring = Math.sqrt(Math.max(0, 1 - y * y));             // circle radius at y
    const a    = (2 * Math.PI * i) / GOLDEN;
    const x    = Math.cos(a) * ring;
    const z    = Math.sin(a) * ring;

    return {
      key: i,
      src: images[i],          // 1:1 with the image list — no modulo, no repeats
      // Inverse of  rotateY(theta) rotateX(phi) translateZ(R)  applied to (0,0,R),
      // which lands at R·(cos·sin, -sin, cos·cos) — so phi comes from y and theta
      // from the x/z bearing.
      theta: Math.atan2(x, z) * DEG,
      phi: -Math.asin(y) * DEG,
      // Three shells rather than one: a single radius reads as a flat ring of heads,
      // while staggered depth reads as a volume.
      depth: 0.74 + (i % 3) * 0.17,
      width: 50 + r(1) * 38,
      // A little dutch angle so the crowd looks casual, not mounted on pins. This is
      // a rotate about the VIEW axis, applied on the img itself, so it cannot
      // disturb the billboard cancellation above it.
      tilt: (r(2) * 2 - 1) * 9,
      // Depth haze — the far shells sit back a little.
      fade: (0.55 + r(3) * 0.45) * opacity,
    };
  }), [total, images, opacity]);

  return (
    <div className="ff-field" style={{ "--spin": `${spin}s` }} aria-hidden="true">
      <div className="ff-spin">
        {faces.map(f => (
          <div key={f.key} className="ff-node"
               style={{ transform: `rotateY(${f.theta}deg) rotateX(${f.phi}deg) translateZ(calc(var(--R) * ${f.depth}))` }}>
            <div className="ff-un"
                 style={{ transform: `rotateX(${-f.phi}deg) rotateY(${-f.theta}deg)` }}>
              <div className="ff-bb" style={{ width: f.width, opacity: f.fade }}>
                <img src={f.src} alt="" loading="eager" draggable={false}
                     style={{ transform: `rotate(${f.tilt}deg)` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Gold sparks drifting off the call-to-action. Rendered as siblings of the button
 * inside `.ctaNeon` (which owns `position: relative`), never as children of the
 * button itself — the squircle engine clips a managed button's content to its
 * superellipse, which would swallow every spark the moment it left the edge.
 */
export function CtaSparks({ count = 16 }) {
  const sparks = useMemo(() => Array.from({ length: count }, (_, i) => {
    const r = (salt) => rnd(i + 101, salt);
    const dur = 1.9 + r(1) * 1.6;
    const top = r(7) < 0.5;              // leaves from the top edge, or the bottom
    return {
      key: i,
      left: 2 + r(2) * 96,               // along the button's width
      size: 3 + r(3) * 4,
      dx: (r(4) * 2 - 1) * 50,           // sideways wander on the way out
      // Sparks travel AWAY from the edge they left: up off the top, down off the
      // bottom. Sending the bottom ones upward drags them across the button's face,
      // which reads as dirt on the button rather than sparks off it.
      dy: (top ? -1 : 1) * (34 + r(5) * 50),
      dur,
      delay: -r(6) * dur,                // negative → already sparking on frame one
      from: top ? "0%" : "100%",
    };
  }), [count]);

  return (
    <>
      {sparks.map(s => (
        <span key={s.key} className="cta-spark" aria-hidden="true"
              style={{
                left: `${s.left}%`, top: s.from,
                width: s.size, height: s.size,
                animationDuration: `${s.dur}s`, animationDelay: `${s.delay}s`,
                "--sx": `${s.dx}px`, "--sy": `${s.dy}px`,
              }} />
      ))}
    </>
  );
}

// The stylesheet for this screen, exported so MiniApp.jsx injects the SAME text a
// probe page renders — the CSS can never drift from the component that needs it.
export const FIRST_RUN_CSS = `
/* ── Sphere of faces (see miniapp/faces.jsx for the transform algebra) ──
   --R is the shell radius. It is responsive on purpose: a fixed px radius that
   fills a 430px phone leaves a lonely little globe on a tablet.
   ⚠️ .ff-field takes perspective + overflow but NOT preserve-3d — overflow on a
   preserve-3d element flattens the whole scene. */
.ff-field { position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none;
            perspective: 760px; perspective-origin: 50% 44%;
            --R: clamp(230px, 74vw, 430px); }
.ff-spin  { position: absolute; left: 50%; top: 44%; width: 0; height: 0;
            transform-style: preserve-3d;
            animation: ffSpin var(--spin, 64s) linear infinite; }
.ff-node  { position: absolute; transform-style: preserve-3d; }
.ff-un    { position: absolute; transform-style: preserve-3d; }
/* Mirrors .ff-spin exactly — same duration, same linear timing, no delay — so the
   face stays square to the camera for every one of the shell's revolutions. */
.ff-bb    { position: absolute; transform-style: preserve-3d;
            animation: ffBillboard var(--spin, 64s) linear infinite; }
.ff-bb img { display: block; width: 100%; height: auto;
             filter: drop-shadow(0 8px 18px ${C.bgDeep}); }

@keyframes ffSpin {
  from { transform: translateZ(calc(var(--R) * -0.34)) rotateY(0deg); }
  to   { transform: translateZ(calc(var(--R) * -0.34)) rotateY(360deg); }
}
/* The -50% centring must come AFTER the rotation, or each face orbits its own
   corner instead of holding position on the shell. */
@keyframes ffBillboard {
  from { transform: rotateY(0deg) translate(-50%, -50%); }
  to   { transform: rotateY(-360deg) translate(-50%, -50%); }
}

/* ── The call-to-action ──
   Three separate layers so nothing fights the squircle engine: the scale breathe is
   on the OUTER wrapper (the engine measures with offsetWidth, so an ancestor
   transform is safe), the neon halo is a blurred sibling BEHIND the button, and the
   colour flash is a child clipped to the button's own superellipse. The button keeps
   no box-shadow of its own — the engine would convert it to a filter and then fight
   the halo for the same pixels. */
.ctaBreathe { animation: ctaBreathe 2.2s ease-in-out infinite; }
@keyframes ctaBreathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.045)} }

.ctaNeon { position: relative; display: block; }
.cta-halo { position: absolute; left: 50%; top: 50%; width: 130%; height: 190%;
            transform: translate(-50%, -50%); border-radius: 999px; pointer-events: none;
            background: radial-gradient(ellipse at center, ${C.goldLight} 0%, ${C.gold}66 34%, ${C.gold}22 54%, transparent 70%);
            z-index: 0;
            animation: ctaHalo 1.6s ease-in-out infinite; }
@keyframes ctaHalo {
  0%,100% { opacity: 0.34; transform: translate(-50%,-50%) scale(0.94); }
  50%     { opacity: 0.92; transform: translate(-50%,-50%) scale(1.06); }
}
/* Rides inside the button, so the squircle clip shapes it for free. */
.cta-flash { position: absolute; inset: 0; pointer-events: none; border-radius: inherit;
             background: linear-gradient(135deg, ${TALE.title} 0%, ${C.goldLight} 55%, ${TALE.label} 100%);
             animation: ctaFlash 1.6s ease-in-out infinite; }
@keyframes ctaFlash { 0%,100%{opacity:0} 50%{opacity:0.5} }

.cta-spark { position: absolute; border-radius: 999px; pointer-events: none; z-index: 2;
             background: ${TALE.title};
             box-shadow: 0 0 5px ${TALE.spark}, 0 0 11px ${C.goldLight}, 0 0 20px ${C.gold};
             animation-name: ctaSpark; animation-timing-function: ease-out;
             animation-iteration-count: infinite; }
@keyframes ctaSpark {
  0%   { opacity: 0;    transform: translate(0,0) scale(0.5); }
  18%  { opacity: 1;    transform: translate(calc(var(--sx) * 0.2), calc(var(--sy) * 0.18)) scale(1); }
  100% { opacity: 0;    transform: translate(var(--sx), var(--sy)) scale(0.25); }
}

@media (prefers-reduced-motion: reduce) {
  .ff-spin, .ff-bb, .ctaBreathe, .cta-halo, .cta-flash, .cta-spark { animation: none; }
  .cta-spark { opacity: 0; }
  .cta-halo  { opacity: 0.5; }
}
`;
